/**
 * Workflow-based permissions that map to user-facing controls.
 * Backend translates these to endpoint-level permissions internally.
 *
 * This replaces the endpoint-level permission toggles with intuitive
 * workflow controls like "View own vs all schedules".
 */

export type ScopeValue = "own" | "all";
export type ScopeValueWithNone = "none" | "own" | "all";

/**
 * Base interface for all permission types.
 * The index signature allows type-safe dynamic key access.
 */
interface BasePermissions {
  [key: string]: boolean | string;
}

export interface CalendarPermissions extends BasePermissions {
  /** View own vs all schedules & transactions */
  scope: ScopeValue;
  /** Can create and edit time blocks */
  canManageTimeBlocks: boolean;
  /** Can delete time blocks */
  canDeleteTimeBlocks: boolean;
  /** Can create, edit, and delete the location's appointment tags */
  canManageAppointmentTags: boolean;
}

export interface ProductsPermissions extends BasePermissions {
  /** Can see products page */
  canView: boolean;
  /** Can create, edit, and delete products */
  canEdit: boolean;
}

export interface ClientsPermissions extends BasePermissions {
  /** Can view clients tab (includes create/edit) */
  canView: boolean;
  /** Can delete clients */
  canDelete: boolean;
}

export interface PackagesPermissions extends BasePermissions {
  /** Can add, adjust units, edit expiry, and revoke a client's service packages */
  canManageClientPackages: boolean;
}

export interface MarketingPermissions extends BasePermissions {
  /** Can view marketing page */
  canView: boolean;
  /** Can create and edit campaigns */
  canCreate: boolean;
  /** Can cancel pending campaigns */
  canCancel: boolean;
}

export interface ReportsPermissions extends BasePermissions {
  /** See no records, own records, or all records */
  scope: ScopeValueWithNone;
  /** Can download reports */
  canDownload: boolean;
  /** Can open the Sales Summary (location-wide money; on/off only) */
  canViewSalesSummary: boolean;
  /** Earnings Summary: no access, own earnings only, or everyone's */
  earningsSummaryScope: ScopeValueWithNone;
  /** Can share their saved reports with other staff (copies to their list) */
  canShareReports: boolean;
}

export interface PayrollPermissions extends BasePermissions {
  /** Clock in/out self vs all staff */
  clockScope: ScopeValue;
  /** Can sync to payroll */
  canSyncToPayroll: boolean;
}

export interface PaymentsPermissions extends BasePermissions {
  /** Can see payments page (Overview & Devices) */
  canView: boolean;
  /** Can issue partial/full refunds against accepted payments. */
  canIssueRefunds: boolean;
  /** Can add, remove, and rename payment terminals (card readers). */
  canManageTerminals: boolean;
}

export interface CashRegisterPermissions extends BasePermissions {
  /** Can see the Cash Drawer button and drawer UI */
  canView: boolean;
  /** Can open/close drawer and record manual adjustments */
  canManage: boolean;
}

export interface NotificationsPermissions extends BasePermissions {
  /** See own vs all notifications */
  scope: ScopeValue;
}

export interface SettingsNotificationsPermissions extends BasePermissions {
  /**
   * Settings -> Notifications: whose notification preferences this role can
   * edit — nobody's (page hidden), only their own, or everyone's (only Owners
   * edit Owners; enforced by the backend).
   */
  scope: ScopeValueWithNone;
}

export interface SettingsStudioPermissions extends BasePermissions {
  /** Can view studio settings */
  canView: boolean;
  /** Can edit studio settings */
  canEdit: boolean;
  /** Can see billing information */
  canSeeBilling: boolean;
}

export interface SettingsStaffPermissions extends BasePermissions {
  /** See own vs all staff */
  scope: ScopeValue;
  /** Can edit staff settings */
  canEdit: boolean;
}

export interface SettingsServicesPermissions extends BasePermissions {
  /** See own vs all services */
  scope: ScopeValue;
  /** Can create/edit/delete services and assign to staff */
  canManageServices: boolean;
  /** Can view adjustment forms */
  canViewAdjustmentForms: boolean;
  /** Can create/edit/delete adjustment forms */
  canManageAdjustmentForms: boolean;
}

export interface SettingsSSGPermissions extends BasePermissions {
  /** Can access website settings */
  canAccess: boolean;
}

export interface SettingsDepositsPermissions extends BasePermissions {
  /** Can access deposit settings */
  canAccess: boolean;
}

export interface SettingsDiscountsPermissions extends BasePermissions {
  /** Can create and edit discount presets (Settings → Discounts + inline create) */
  canManagePresets: boolean;
  /** Can delete (archive, if used on past tickets) discount presets */
  canDeletePresets: boolean;
}

export interface SettingsCommissionsPermissions extends BasePermissions {
  /** See no commissions, own, or all */
  scope: ScopeValueWithNone;
  /** Can edit/delete commissions */
  canEdit: boolean;
  /** Can assign/reassign the commission earner on a product line item */
  canAssignProductCommission: boolean;
}

export interface SettingsPayrollPermissions extends BasePermissions {
  /** Can access payroll settings */
  canAccess: boolean;
}

export interface StaffTiersPermissions extends BasePermissions {
  /** Can create/rename/reorder tiers + manage a tier's defaults */
  canCreateTiers: boolean;
  /** Can assign/move/remove staff to/from tiers + apply defaults onto them */
  canAssignStaffToTiers: boolean;
  /** Can delete a tier (only when it has no members) */
  canDeleteTiers: boolean;
}

export interface FormsAndWaiversPermissions extends BasePermissions {
  /** Can view client forms (Forms & Waivers tab on client detail) */
  canViewClientForms: boolean;
  /** Can assign, edit drafts, expire, and delete client forms */
  canManageClientForms: boolean;
  /** Can view the Form Templates settings tab */
  canViewTemplates: boolean;
  /** Can create, edit, copy, and delete form templates */
  canManageTemplates: boolean;
}

export interface SalonDocumentsPermissions extends BasePermissions {
  /** Can list, view, and download client documents */
  canViewDocuments: boolean;
  /** Can upload, delete, and revert client documents */
  canManageDocuments: boolean;
}

/**
 * Complete workflow permissions structure.
 * This matches the sectionPermissions returned from GET /access
 */
export interface WorkflowPermissions {
  calendar: CalendarPermissions;
  products: ProductsPermissions;
  clients: ClientsPermissions;
  packages: PackagesPermissions;
  marketing: MarketingPermissions;
  reports: ReportsPermissions;
  payroll: PayrollPermissions;
  payments: PaymentsPermissions;
  cashRegister: CashRegisterPermissions;
  notifications: NotificationsPermissions;
  settingsNotifications: SettingsNotificationsPermissions;
  settingsStudio: SettingsStudioPermissions;
  settingsStaff: SettingsStaffPermissions;
  settingsServices: SettingsServicesPermissions;
  settingsSSG: SettingsSSGPermissions;
  settingsDeposits: SettingsDepositsPermissions;
  settingsDiscounts: SettingsDiscountsPermissions;
  settingsCommissions: SettingsCommissionsPermissions;
  settingsPayroll: SettingsPayrollPermissions;
  staffTiers: StaffTiersPermissions;
  formsAndWaivers: FormsAndWaiversPermissions;
  salonDocuments: SalonDocumentsPermissions;
}

export type WorkflowKey = keyof WorkflowPermissions;

export type Role = "Owner" | "Manager" | "Employee" | "Assistant";

/**
 * Role permissions with override tracking
 */
export interface RoleWorkflowPermissions {
  role: Role;
  permissions: WorkflowPermissions;
  hasOverrides: boolean;
}

/**
 * Response from GET /business-locations/{salon_id}/role-permissions
 */
export interface GetRolePermissionsResponse {
  salon_id: string;
  permissions: Record<Role, WorkflowPermissions>;
  overrides: Record<Role, Partial<WorkflowPermissions>>;
}

/**
 * Request for PUT /business-locations/{salon_id}/role-permissions (one role per request; no /batch path)
 */
export interface BatchUpdatePermissionsRequest {
  role: Role;
  permissions: WorkflowPermissions;
}

/**
 * Response from PUT /business-locations/{salon_id}/role-permissions
 */
export interface BatchUpdatePermissionsResponse {
  success: boolean;
  role: Role;
  permissions: WorkflowPermissions;
  overrides: Partial<WorkflowPermissions>;
  updated_at: string;
}

/**
 * Response from DELETE /business-locations/{salon_id}/role-permissions/{role}
 */
export interface ResetPermissionsResponse {
  success: boolean;
  role: Role;
  message: string;
  deleted_count: number;
}

/**
 * Permission category for UI grouping
 */
export interface PermissionCategory {
  id: string;
  label: string;
  description: string;
  workflows: WorkflowKey[];
}

/**
 * Permission control configuration for UI rendering
 */
export interface PermissionControlConfig {
  workflow: WorkflowKey;
  key: string;
  label: string;
  description: string;
  type: "toggle" | "scope" | "scopeWithNone";
  options?: { value: string; label: string }[];
}

/**
 * Type-safe helper to get a permission value from a workflow.
 * All permission interfaces extend BasePermissions with an index signature,
 * so dynamic key access is type-safe.
 */
export function getPermissionValue(
  permissions: WorkflowPermissions,
  workflow: WorkflowKey,
  key: string,
): boolean | string | undefined {
  const workflowPerms = permissions[workflow];
  if (!workflowPerms) {
    return undefined;
  }
  return workflowPerms[key];
}

/**
 * Type-safe helper to set a permission value on a workflow.
 */
export function setPermissionValue<T extends WorkflowKey>(
  permissions: WorkflowPermissions,
  workflow: T,
  key: string,
  value: boolean | string,
): WorkflowPermissions {
  return {
    ...permissions,
    [workflow]: {
      ...permissions[workflow],
      [key]: value,
    },
  };
}

/**
 * Categories for organizing permissions in the UI
 */
export const WORKFLOW_CATEGORIES: PermissionCategory[] = [
  {
    id: "calendar",
    label: "Calendar",
    description: "View and manage schedules, appointments, and time off",
    workflows: ["calendar"],
  },
  {
    id: "products",
    label: "Products",
    description: "View and manage product inventory",
    workflows: ["products"],
  },
  {
    id: "clients",
    label: "Client Profiles",
    description: "View and manage client information",
    workflows: ["clients"],
  },
  {
    id: "packages",
    label: "Packages",
    description: "Grant, adjust, and revoke client service packages",
    workflows: ["packages"],
  },
  {
    id: "marketing",
    label: "Marketing",
    description: "Create and manage marketing campaigns",
    workflows: ["marketing"],
  },
  {
    id: "reports",
    label: "Reports",
    description: "View reports and analytics",
    workflows: ["reports"],
  },
  {
    id: "payments",
    label: "Payments",
    description: "Process payments and view payment history",
    workflows: ["payments"],
  },
  {
    id: "notifications",
    label: "Notifications",
    description: "Manage which notifications staff receive",
    workflows: ["settingsNotifications"],
  },
  {
    id: "business_setup",
    label: "Business Setup",
    description: "Studio settings, deposits, and website configuration",
    workflows: ["settingsStudio", "settingsDeposits", "settingsSSG"],
  },
  {
    id: "staff",
    label: "Staff",
    description: "View and manage staff members",
    workflows: ["settingsStaff"],
  },
  {
    id: "catalog",
    label: "Service Catalog",
    description: "Services and adjustment forms",
    workflows: ["settingsServices"],
  },
  {
    id: "discounts",
    label: "Discounts",
    description: "Discount presets applied at checkout",
    workflows: ["settingsDiscounts"],
  },
  {
    id: "commissions",
    label: "Commissions",
    description: "View and manage commission settings",
    workflows: ["settingsCommissions"],
  },
  {
    id: "staff_tiers",
    label: "Staff Tiers",
    description: "Create tiers, assign staff, and manage tier defaults",
    workflows: ["staffTiers"],
  },
  {
    id: "payroll",
    label: "Payroll",
    description: "Time tracking and payroll settings",
    workflows: ["payroll", "settingsPayroll"],
  },
  {
    id: "forms_and_waivers",
    label: "Forms & Waivers",
    description: "Client forms, waivers, and form templates",
    workflows: ["formsAndWaivers"],
  },
  {
    id: "salon_documents",
    label: "Client Documents",
    description: "Upload, view, and manage clinical files attached to clients",
    workflows: ["salonDocuments"],
  },
];

/**
 * Permission controls configuration for each workflow
 */
export const PERMISSION_CONTROLS: PermissionControlConfig[] = [
  // Calendar
  {
    workflow: "calendar",
    key: "scope",
    label: "Schedule visibility",
    description: "Which staff schedules can this role see?",
    type: "scope",
    options: [
      { value: "own", label: "Own schedule only" },
      { value: "all", label: "All staff schedules" },
    ],
  },
  {
    workflow: "calendar",
    key: "canManageTimeBlocks",
    label: "Can manage time blocks",
    description: "Create and edit time off, breaks, and blocks",
    type: "toggle",
  },
  {
    workflow: "calendar",
    key: "canDeleteTimeBlocks",
    label: "Can delete time blocks",
    description: "Remove time blocks from schedules",
    type: "toggle",
  },
  {
    workflow: "calendar",
    key: "canManageAppointmentTags",
    label: "Can manage appointment tags",
    description:
      "Create, edit, and delete the tags staff apply to appointments",
    type: "toggle",
  },

  // Products
  {
    workflow: "products",
    key: "canView",
    label: "Can view products",
    description: "Access the products page",
    type: "toggle",
  },
  {
    workflow: "products",
    key: "canEdit",
    label: "Can edit products",
    description: "Create, edit, delete, and manage product inventory",
    type: "toggle",
  },

  // Clients
  {
    workflow: "clients",
    key: "canView",
    label: "Can view clients",
    description: "Access the clients page (includes create/edit)",
    type: "toggle",
  },
  {
    workflow: "clients",
    key: "canDelete",
    label: "Can delete clients",
    description: "Remove clients from the system",
    type: "toggle",
  },

  // Packages
  {
    workflow: "packages",
    key: "canManageClientPackages",
    label: "Can manage client packages",
    description:
      "Add, adjust units, edit expiry, and revoke packages on a client profile",
    type: "toggle",
  },

  // Marketing
  {
    workflow: "marketing",
    key: "canView",
    label: "Can view marketing",
    description: "Access the marketing page",
    type: "toggle",
  },
  {
    workflow: "marketing",
    key: "canCreate",
    label: "Can create campaigns",
    description: "Create and edit marketing campaigns",
    type: "toggle",
  },
  {
    workflow: "marketing",
    key: "canCancel",
    label: "Can cancel campaigns",
    description: "Cancel pending marketing campaigns",
    type: "toggle",
  },

  // Reports
  {
    workflow: "reports",
    key: "scope",
    label: "Report access",
    description: "Which records can this role see?",
    type: "scopeWithNone",
    options: [
      { value: "none", label: "No access" },
      { value: "own", label: "Own records only" },
      { value: "all", label: "All records" },
    ],
  },
  {
    workflow: "reports",
    key: "canDownload",
    label: "Can download reports",
    description: "Download report data as files",
    type: "toggle",
  },
  {
    workflow: "reports",
    key: "canViewSalesSummary",
    label: "Can view the Sales Summary",
    description:
      "Location-wide payments, refunds and totals by category and payment method",
    type: "toggle",
  },
  {
    workflow: "reports",
    key: "earningsSummaryScope",
    label: "Earnings Summary access",
    description: "Whose sales, commissions and tips can this role see?",
    type: "scopeWithNone",
    options: [
      { value: "none", label: "No access" },
      { value: "own", label: "Own earnings only" },
      { value: "all", label: "Everyone's earnings" },
    ],
  },
  {
    workflow: "reports",
    key: "canShareReports",
    label: "Can share reports",
    description:
      "Send a copy of a saved report to other staff, keep it in sync and revoke it",
    type: "toggle",
  },

  // Payments
  {
    workflow: "payments",
    key: "canView",
    label: "Can view payments",
    description: "Access the payments page (Overview & Devices)",
    type: "toggle",
  },
  {
    workflow: "payments",
    key: "canIssueRefunds",
    label: "Can issue refunds",
    description:
      "Refund accepted payments (card & non-card) from a transaction",
    type: "toggle",
  },
  {
    workflow: "payments",
    key: "canManageTerminals",
    label: "Can manage payment terminals",
    description:
      "Add, remove, rename, and configure card reader devices (Devices tab)",
    type: "toggle",
  },

  // Notifications. The feed's `notifications.scope` has no control here: each
  // person's feed is now what their notification settings deliver to them. The
  // value is kept in WorkflowPermissions/defaults so it still round-trips.
  {
    workflow: "settingsNotifications",
    key: "scope",
    label: "Notification settings",
    description: "Whose notification settings can this role edit?",
    type: "scopeWithNone",
    options: [
      { value: "none", label: "No access" },
      { value: "own", label: "Own settings only" },
      { value: "all", label: "All staff" },
    ],
  },

  // Settings - Studio
  {
    workflow: "settingsStudio",
    key: "canView",
    label: "Can view studio settings",
    description: "Access studio configuration",
    type: "toggle",
  },
  {
    workflow: "settingsStudio",
    key: "canEdit",
    label: "Can edit studio settings",
    description: "Modify studio configuration",
    type: "toggle",
  },
  {
    workflow: "settingsStudio",
    key: "canSeeBilling",
    label: "Can see billing",
    description: "View billing and subscription information",
    type: "toggle",
  },

  // Settings - Staff
  {
    workflow: "settingsStaff",
    key: "scope",
    label: "Staff visibility",
    description: "Which staff members can this role see?",
    type: "scope",
    options: [
      { value: "own", label: "Own profile only" },
      { value: "all", label: "All staff members" },
    ],
  },
  {
    workflow: "settingsStaff",
    key: "canEdit",
    label: "Can edit staff",
    description: "Modify staff member settings",
    type: "toggle",
  },

  // Settings - Services
  {
    workflow: "settingsServices",
    key: "scope",
    label: "Service visibility",
    description: "Which services can this role see?",
    type: "scope",
    options: [
      { value: "own", label: "Assigned services only" },
      { value: "all", label: "All services" },
    ],
  },
  {
    workflow: "settingsServices",
    key: "canManageServices",
    label: "Can manage services",
    description: "Create, edit, delete services and assign to staff",
    type: "toggle",
  },
  {
    workflow: "settingsServices",
    key: "canViewAdjustmentForms",
    label: "Can view adjustment forms",
    description: "See service adjustment forms",
    type: "toggle",
  },
  {
    workflow: "settingsServices",
    key: "canManageAdjustmentForms",
    label: "Can manage adjustment forms",
    description: "Create, edit, delete adjustment forms",
    type: "toggle",
  },

  // Settings - Discounts
  {
    workflow: "settingsDiscounts",
    key: "canManagePresets",
    label: "Can create & edit discounts",
    description:
      "Add and edit discount presets in Settings, and create a new one while applying a discount to a ticket",
    type: "toggle",
  },
  {
    workflow: "settingsDiscounts",
    key: "canDeletePresets",
    label: "Can delete discounts",
    description:
      "Remove discount presets (ones already used on past tickets are archived and kept for history)",
    type: "toggle",
  },

  // Settings - SSG
  {
    workflow: "settingsSSG",
    key: "canAccess",
    label: "Can access website settings",
    description: "Manage website configuration",
    type: "toggle",
  },

  // Settings - Deposits
  {
    workflow: "settingsDeposits",
    key: "canAccess",
    label: "Can access deposit settings",
    description: "Manage deposit configuration",
    type: "toggle",
  },

  // Settings - Commissions
  {
    workflow: "settingsCommissions",
    key: "scope",
    label: "Commission visibility",
    description: "Which commissions can this role see?",
    type: "scopeWithNone",
    options: [
      { value: "none", label: "No access" },
      { value: "own", label: "Own commissions only" },
      { value: "all", label: "All commissions" },
    ],
  },
  {
    workflow: "settingsCommissions",
    key: "canEdit",
    label: "Can edit commissions",
    description: "Modify commission settings",
    type: "toggle",
  },
  {
    workflow: "settingsCommissions",
    key: "canAssignProductCommission",
    label: "Can assign product sale commission",
    description:
      "Choose which staff member earns commission on a product sale (transaction & quick-sale screens)",
    type: "toggle",
  },

  // Staff Tiers
  {
    workflow: "staffTiers",
    key: "canCreateTiers",
    label: "Can create & configure tiers",
    description: "Create, rename, reorder tiers and manage their defaults",
    type: "toggle",
  },
  {
    workflow: "staffTiers",
    key: "canAssignStaffToTiers",
    label: "Can assign staff to tiers",
    description:
      "Assign, move, or remove staff and apply tier defaults to them",
    type: "toggle",
  },
  {
    workflow: "staffTiers",
    key: "canDeleteTiers",
    label: "Can delete tiers",
    description: "Delete a tier when it has no assigned staff",
    type: "toggle",
  },

  // Settings - Payroll
  {
    workflow: "settingsPayroll",
    key: "canAccess",
    label: "Can access payroll settings",
    description: "Access payroll configuration",
    type: "toggle",
  },

  // Payroll (time tracking)
  {
    workflow: "payroll",
    key: "clockScope",
    label: "Clock in/out scope",
    description: "Who can this role clock in/out?",
    type: "scope",
    options: [
      { value: "own", label: "Self only" },
      { value: "all", label: "All staff members" },
    ],
  },
  {
    workflow: "payroll",
    key: "canSyncToPayroll",
    label: "Can sync to payroll",
    description: "Sync data to external payroll system",
    type: "toggle",
  },

  // Forms & Waivers
  {
    workflow: "formsAndWaivers",
    key: "canViewClientForms",
    label: "Can view client forms",
    description: "See the Forms & Waivers tab on a client's profile",
    type: "toggle",
  },
  {
    workflow: "formsAndWaivers",
    key: "canManageClientForms",
    label: "Can manage client forms",
    description: "Assign, edit drafts, expire, and delete forms on clients",
    type: "toggle",
  },
  {
    workflow: "formsAndWaivers",
    key: "canViewTemplates",
    label: "Can view form templates",
    description: "Access the Form Templates settings tab",
    type: "toggle",
  },
  {
    workflow: "formsAndWaivers",
    key: "canManageTemplates",
    label: "Can manage form templates",
    description: "Create, edit, copy, and delete form templates",
    type: "toggle",
  },

  // Client Documents
  {
    workflow: "salonDocuments",
    key: "canViewDocuments",
    label: "Can view client documents",
    description: "List, preview, and download files on a client's profile",
    type: "toggle",
  },
  {
    workflow: "salonDocuments",
    key: "canManageDocuments",
    label: "Can manage client documents",
    description: "Upload, delete, and revert versions of client documents",
    type: "toggle",
  },
];
